import { isTransientChunkError, arrayBufferToBase64 } from './apis/streaming'

/**
 * chunk POST が瞬断 (network/timeout) で失敗した時、 同 seq / 同 payload を 1 回だけ
 * 再送する前の backoff. back の seq dedup により再送は冪等なので、 短い待機で
 * 「届いていなければ処理される」を狙う.
 */
export const CHUNK_RETRY_BACKOFF_MS = 300

/**
 * 1 POST にまとめ送り (coalesce) する pending chunk の最大バイト数.
 *
 * 上限の根拠は Google Chirp streaming の 1 message あたり音声 25,600 bytes 制限:
 * backend runner は 1 POST の payload を分割せずそのまま 1 message として Chirp に
 * feed するため、 これを超える連結を送ると
 * `Audio chunk can be of a maximum of 25600 bytes` の InvalidArgument で stream が
 * 切られる (本番 Sentry KAKERU-DJANGO-2GG/2GF。 旧値 160,000 はこの制限の見落とし)。
 *
 * 24,000 = 100ms chunk (3,200 bytes) × 7 個 + margin (16kHz/mono/LINEAR16 で音声
 * 700ms 分)。 RTT < 700ms なら backlog はまとめ送りで実時間より速く回収できる。
 * 超過分は次の POST で送る (直列なので順序は維持される)。
 */
export const MAX_COALESCED_BYTES = 24_000

export type ChunkSender = (seq: number, audioBase64: string) => Promise<unknown>

export type ChunkSendQueueOptions = {
  /** 実送信関数. 本番は postAudioChunk(sessionId, seq, b64) を bind して渡す. */
  send: ChunkSender
  /**
   * 回復不能失敗 (非 transient エラー / retry 失敗) で 1 回だけ呼ばれる.
   * caller (useRecordingEngine) は switchToFallback に繋ぐ.
   */
  onFatal: () => void
  /** テスト用 override. 省略時 isTransientChunkError. */
  isTransient?: (err: unknown) => boolean
  /** テスト用 override (ms). 省略時 CHUNK_RETRY_BACKOFF_MS. */
  retryBackoffMs?: number
  /** テスト用 override (bytes). 省略時 MAX_COALESCED_BYTES. */
  maxCoalescedBytes?: number
}

/**
 * streaming chunk の直列送信キュー.
 *
 * 旧実装は worklet chunk ごとに postAudioChunk を await せず並行発射しており、
 * (a) RTT > 100ms で複数 in-flight になった際の HTTP 到着順逆転、
 * (b) 瞬断 retry (300ms backoff) の同 seq 遅延挿入、
 * の 2 経路で backend への到着順 (= Chirp への feed 順 = 転写の時間軸) が
 * 崩れうる問題があった. backend は seq を dedup にしか使わず並べ替えないため
 * (session_runner.enqueue_chunk 参照)、 順序保証は送信側の責務になる.
 *
 * 本キューは quickrec デスクトップ (StreamingUploader) と同型の
 * 「in-flight 常に 1 本」の直列送信に置き換えた上で、 直列化の弱点である
 * 送信レート不足 (RTT > 100ms が続くと backlog が溜まり実時間から遅れる) を
 * まとめ送り (coalesce) で解消する: 送信待ちが複数溜まっていたら連結して
 * 1 POST (最大 MAX_COALESCED_BYTES = 音声 700ms 分) にし、 レイテンシスパイク後も
 * 実時間より速く追いつく (RTT < 700ms の間、 1 RTT で 700ms 分回収).
 *
 * retry の冪等性: 失敗した POST は「同 seq・同 payload」のまま再送する.
 * 溜まった新 chunk を混ぜて payload を変えると、 1 回目が実は届いていた場合に
 * seq dedup で新 chunk ごと捨てられてしまうため、 再送 payload は不変が必須.
 *
 * seq は「POST ごと」の単調増加 (chunk ごとではない). backend は順序・欠番を
 * 見ないため、 dedup キーとして一意単調でありさえすればよい.
 */
export class ChunkSendQueue {
  private readonly send: ChunkSender
  private readonly onFatal: () => void
  private readonly isTransient: (err: unknown) => boolean
  private readonly retryBackoffMs: number
  private readonly maxCoalescedBytes: number

  private pending: ArrayBuffer[] = []
  private seq = 0
  private busy = false
  private donePromise: Promise<void> = Promise.resolve()
  private fatal = false
  private aborted = false

  constructor(options: ChunkSendQueueOptions) {
    this.send = options.send
    this.onFatal = options.onFatal
    this.isTransient = options.isTransient ?? isTransientChunkError
    this.retryBackoffMs = options.retryBackoffMs ?? CHUNK_RETRY_BACKOFF_MS
    this.maxCoalescedBytes = options.maxCoalescedBytes ?? MAX_COALESCED_BYTES
  }

  /** 回復不能失敗が起きたか (onFatal 発火済). */
  get failed(): boolean {
    return this.fatal
  }

  /** worklet chunk を送信待ちに積み、 sender loop が止まっていれば起動する. */
  enqueue(buffer: ArrayBuffer): void {
    if (this.fatal || this.aborted) return
    this.pending.push(buffer)
    this.pump()
  }

  /**
   * 送信待ち + in-flight をすべて掃き切るまで待つ.
   * caller は worklet close-ack (= 以降新規 chunk が来ない保証) の後に呼ぶこと.
   * fatal / abort で打ち切られた場合も resolve する (throw しない).
   */
  async drain(): Promise<void> {
    // enqueue → pump の再起動があり得るため、 idle かつ空になるまで待ち直す.
    // 各 iteration 先頭の pump() で「idle なのに pending が残る」状態を必ず解消する.
    for (;;) {
      this.pump()
      await this.donePromise
      if (this.fatal || this.aborted) return
      if (!this.busy && this.pending.length === 0) return
    }
  }

  /**
   * 送信を静かに打ち切る (onFatal は呼ばない). fallback 切替や teardown で、
   * もはや不要な chunk POST を止めるために呼ぶ. 冪等.
   */
  abort(): void {
    this.aborted = true
    this.pending = []
  }

  private pump(): void {
    if (this.busy || this.fatal || this.aborted) return
    this.busy = true
    this.donePromise = this.run().finally(() => {
      this.busy = false
    })
  }

  private async run(): Promise<void> {
    while (this.pending.length > 0 && !this.fatal && !this.aborted) {
      const payload = this.takeBatch()
      const seq = this.seq++
      const b64 = arrayBufferToBase64(payload)
      try {
        await this.send(seq, b64)
      } catch (err) {
        if (this.aborted) return
        if (!this.isTransient(err)) {
          console.warn(
            '[transcription-dispatch] ChunkSendQueue: 非transient エラー → fatal',
            { seq, err }
          )
          this.fireFatal()
          return
        }
        // 瞬断: 同 seq・同 payload を 1 回だけ再送 (dedup で冪等).
        // 直列なので後続 chunk が先行することはなく、 順序は崩れない.
        await new Promise((r) => setTimeout(r, this.retryBackoffMs))
        if (this.aborted) return
        try {
          await this.send(seq, b64)
        } catch {
          console.warn(
            '[transcription-dispatch] ChunkSendQueue: 再送も失敗 → fatal',
            { seq }
          )
          this.fireFatal()
          return
        }
      }
    }
  }

  /** pending 先頭から maxCoalescedBytes 以内 (最低 1 個) を連結して取り出す. */
  private takeBatch(): ArrayBuffer {
    let count = 0
    let bytes = 0
    while (count < this.pending.length) {
      const size = this.pending[count].byteLength
      if (count > 0 && bytes + size > this.maxCoalescedBytes) break
      bytes += size
      count++
    }
    const items = this.pending.splice(0, count)
    if (items.length === 1) {
      return items[0]
    }
    // レイテンシスパイク後の追いつき (backlog 一括回収). 500ms 分超の
    // まとめ送りだけ観測ログに残す (正常時の 1-2 個 coalesce は出さない).
    if (items.length > 5) {
      console.info('[transcription-dispatch] ChunkSendQueue: coalesced', {
        chunks: items.length,
        bytes,
      })
    }
    const merged = new Uint8Array(bytes)
    let offset = 0
    for (const item of items) {
      merged.set(new Uint8Array(item), offset)
      offset += item.byteLength
    }
    return merged.buffer
  }

  private fireFatal(): void {
    if (this.fatal) return
    this.fatal = true
    this.pending = []
    try {
      this.onFatal()
    } catch {
      // onFatal (switchToFallback) の失敗で sender loop を壊さない
    }
  }
}
